import { Combobox, Label, MultiSelect, useStateWithSetAction } from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { FormResponseValue } from "@tellescope/types-models";
import { TellescopeFormFieldBaseProps } from "../types";
import {
  resolveFieldOptionDetails,
  resolveVisibleChoices,
  translateFieldOptionLabels,
  type FormTranslationMap,
} from "../utils";
import type { NextFieldLogicOptions } from "../utils/types";
import { useTranslation } from "react-i18next";
import { FieldDescription } from "../FieldDescription";
import { useMemo } from "react";

export interface DropdownFieldProps extends TellescopeFormFieldBaseProps<"Dropdown"> {
  formResponses?: FormResponseValue[];
  logicOptions?: NextFieldLogicOptions;
  translations?: FormTranslationMap;
}

export const DropdownField = ({
  title,
  description,
  htmlDescription,
  placeholder,
  value = [],
  onChange,
  className,
  options,
  onBlur,
  error,
  required,
  formResponses,
  logicOptions,
  autoFocus,
  disabled,
  translations,
}: DropdownFieldProps) => {
  const { t } = useTranslation("forms");
  const [open, setOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      onBlur?.();
    }
  });

  const allowOther = !!options?.other;
  const optionDetails = useMemo(
    () =>
      resolveVisibleChoices({
        optionDetails: resolveFieldOptionDetails(options),
        responses: formResponses ?? [],
        logicOptions,
      }),
    [options, formResponses, logicOptions]
  );

  const resolvedPlaceholder = placeholder ?? t("Select item...");

  // `value` stays the authored English — it is what gets stored and what branching and scoring
  // compare against. Only `label`/`description` are translated. A free-text Other answer is the
  // enduser's own typing and is never translated.
  const selectableOptions = useMemo(
    () =>
      translateFieldOptionLabels(optionDetails, translations).map((option) => ({
        value: option.option,
        label: option.label,
        description: option.translatedDescription,
      })),
    [optionDetails, translations]
  );

  // A selected value that is not one of the choices is a custom answer, so it needs its own
  // entry to stay visible and removable in the list. Matched against every authored choice, not
  // just the visible ones, so a choice hidden by conditional logic is not shown as a custom answer.
  const knownOptionValues = useMemo(() => resolveFieldOptionDetails(options).map((o) => o.option), [options]);
  const comboboxOptions = useMemo(
    () =>
      value.reduce(
        (acc, v) => (knownOptionValues.includes(v) ? acc : [...acc, { value: v, label: v, description: undefined }]),
        selectableOptions
      ),
    [selectableOptions, value, knownOptionValues]
  );

  const renderCreateEntry = (inputValue: string) => t('Add "{{value}}"', { value: inputValue });

  return (
    <div className="space-y-1 w-full">
      <Label required={required}>{title || resolvedPlaceholder}</Label>
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      {options?.radio ? (
        <Combobox<string>
          value={value[0] ? { value: value[0], label: value[0] } : undefined}
          onChange={(selected) => onChange(selected ? [selected.value] : [])}
          options={comboboxOptions}
          placeholder={resolvedPlaceholder}
          searchPlaceholder={t("Search...")}
          emptyText={t("No results found")}
          clearable
          className={cn(className, !!error && "[&>button]:border-destructive")}
          open={open}
          setOpen={setOpen}
          autoFocus={autoFocus}
          disabled={disabled}
          creator={
            allowOther
              ? {
                  render: renderCreateEntry,
                  transform: (inputValue) => ({ value: inputValue, label: inputValue }),
                }
              : undefined
          }
        />
      ) : (
        <MultiSelect
          value={value}
          onValueChange={onChange}
          onBlur={onBlur}
          className={cn("w-full", className, !!error && "border-destructive")}
          placeholder={resolvedPlaceholder}
          searchPlaceholder={t("Search...")}
          autoFocus={autoFocus}
          disabled={disabled}
          options={comboboxOptions.map(({ value: optionValue, label, description: optionDescription }) => ({
            value: optionValue,
            label: `${label} ${optionDescription ?? ""}`.trim(),
          }))}
          searchKeywords={(option) => [option.value, option.label]}
          creator={
            allowOther
              ? {
                  render: renderCreateEntry,
                  transform: (inputValue) => inputValue,
                  // The custom value is already carried by the answer, which is what builds the
                  // option list, so there is no separate option store to add it to.
                  onCreate: () => {},
                }
              : undefined
          }
        />
      )}

      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
